import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Smartphone,
  Apple,
  Tv,
  ArrowRight,
  ArrowUpRight,
  Check,
  Star,
  Menu,
  X,
  Lock,
  Sparkles,
  HandCoins,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_0dc742ce-6c69-4346-a449-dd98ef3c81ad/artifacts/mnjw0asf_Safety-Pay-Logo.png";

const NAV_LINKS = [
  { label: "Products", href: "#products" },
  { label: "Why Us", href: "#why-us" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

const PRODUCTS = [
  {
    id: "pro-max",
    tag: "Android",
    title: "Air Lock Pro / Max",
    desc: "An Android app for retailers to manage financed customers. If EMIs are delayed, the retailer can quickly take control of the device.",
    features: [
      "Remote device lock & unlock",
      "EMI-based automated reminders",
      "Multi-store retailer dashboard",
    ],
    Icon: Smartphone,
    accent: "#4B3F9E",
  },
  {
    id: "lite",
    tag: "Appliances",
    title: "Air Lock Lite",
    desc: "Offer loyal customers easy EMI instalment options for household appliances — making shopping affordable while you grow your store.",
    features: [
      "Easy in-store EMI workflow",
      "Customer KYC & repayment tracking",
      "Works for TVs, ACs, washing machines",
    ],
    Icon: Tv,
    accent: "#1C9AA0",
  },
  {
    id: "iplus",
    tag: "iPhone",
    title: "Air Lock IPlus",
    desc: "iPhone Locker for retailers. Track financed customers and quickly control the iPhone if EMIs are overdue to ensure on-time payments.",
    features: [
      "Configuration-profile based lock",
      "MDM-grade payment enforcement",
      "Real-time EMI status sync",
    ],
    Icon: Apple,
    accent: "#0A0B10",
  },
];

const REASONS = [
  {
    title: "Quality Service",
    desc: "Our success comes from a commitment to delivering high-quality service. Solutions that meet and exceed client expectations.",
    Icon: Sparkles,
  },
  {
    title: "Affordability",
    desc: "We strive to provide real value with competitive pricing and uncompromised quality. Built for Indian retailers.",
    Icon: HandCoins,
  },
  {
    title: "Trustworthiness",
    desc: "Trust is key. We've earned it by consistently keeping our promises, delivering reliable results and protecting client interests.",
    Icon: ShieldCheck,
  },
];

const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    role: "Retail Partner, Delhi NCR",
    quote:
      "We're very thankful for their expertise and dedication. They solved our business problems with innovative ideas, and the results were excellent.",
  },
  {
    name: "Ankit Verma",
    role: "Mobile Store Owner, Lucknow",
    quote:
      "Their commitment and skills made a lasting impact. They found creative solutions to our mobile business challenges, which made a big difference.",
  },
  {
    name: "Sneha Patel",
    role: "Distributor, Ahmedabad",
    quote:
      "They used innovative ideas to solve our mobile business problems, leading to amazing improvements. Their work has helped our business grow.",
  },
  {
    name: "Rahul Mehta",
    role: "Multi-store Operator, Pune",
    quote:
      "We were highly impressed by their hard work and extensive knowledge. Their creative problem-solving had a huge, positive impact on our business growth.",
  },
];

const STATS = [
  { value: "10K+", label: "Devices secured" },
  { value: "98%", label: "On-time EMI rate" },
  { value: "500+", label: "Partner retailers" },
  { value: "24/7", label: "Support" },
];

const SectionLabel = ({ children, num }) => (
  <div
    className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#4B3F9E]"
    data-testid="section-label"
  >
    <span className="font-mono text-[#0A0B10]/40">{num}</span>
    <span className="h-px w-8 bg-[#4B3F9E]/40" />
    {children}
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#top"
          className="flex items-center gap-2"
          data-testid="header-logo-link"
        >
          <div className="flex h-50 w-50 items-center justify-center rounded-md ">
            <img
              src={LOGO_URL}
              alt="SafetyPay"
              className="h-7 w-50 object-contain"
            />
          </div>
        </a>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-full px-4 py-2 text-sm font-medium text-[#0A0B10]/70 transition hover:bg-[#0A0B10]/5 hover:text-[#0A0B10]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="tel:+919266866302"
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-medium text-[#0A0B10]/70 hover:text-[#0A0B10]"
          >
            <Phone className="h-4 w-4" />
            +91 9266866302
          </a>
          <a
            href="#contact"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 rounded-md bg-[#4B3F9E] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#3A307D]"
          >
            Get Started
            <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          aria-label="Open menu"
          className="rounded-md p-2 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div
          className="border-t border-slate-200 bg-white lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="rounded-md px-3 py-3 text-base font-medium text-[#0A0B10]/80 hover:bg-[#0A0B10]/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-button"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-[#4B3F9E] px-5 py-3 text-sm font-semibold text-white"
            >
              Get Started <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden bg-white pt-32 lg:pt-40"
  >
    <div className="absolute inset-0 -z-10 grid-lines opacity-70" />
    <div className="pointer-events-none absolute -top-24 right-[-10%] -z-10 h-[500px] w-[500px] rounded-full bg-[#4B3F9E]/10 blur-3xl" />
    <div className="pointer-events-none absolute bottom-[-200px] left-[-10%] -z-10 h-[420px] w-[420px] rounded-full bg-[#1C9AA0]/10 blur-3xl" />

    <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <div
            className="animate-float-up inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0B10]/70 backdrop-blur"
            data-testid="hero-pill"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#1C9AA0]" />
            EMI Locker Solutions
          </div>
          <h1
            data-testid="hero-headline"
            className="animate-float-up delay-100 mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter text-[#0A0B10] sm:text-6xl lg:text-7xl"
          >
            Secure your financed devices.{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#4B3F9E]">
                Empower retail
              </span>
              <span className="absolute bottom-1 left-0 -z-0 h-3 w-full bg-[#FFC107]/60" />
            </span>{" "}
            growth.
          </h1>
          <p
            data-testid="hero-subheadline"
            className="animate-float-up delay-200 mt-6 max-w-2xl text-lg leading-relaxed text-[#474D57]"
          >
            The ultimate EMI lock platform for Indian retailers. Manage financed
            Android phones, iPhones and home appliances — and ensure on-time
            payments, every cycle.
          </p>
          <div className="animate-float-up delay-300 mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#products"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-md bg-[#0A0B10] px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-[#1C9AA0]"
            >
              Explore solutions
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </a>
            <a
              href="#contact"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-md border border-[#0A0B10]/15 bg-white px-6 py-3.5 text-sm font-semibold text-[#0A0B10] transition hover:border-[#0A0B10]"
            >
              Contact sales
            </a>
          </div>

          <dl
            className="animate-float-up delay-400 mt-14 grid grid-cols-2 gap-y-6 sm:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label} className="border-l border-[#0A0B10]/15 pl-4">
                <dt className="font-display text-3xl font-black tracking-tight text-[#0A0B10]">
                  {s.value}
                </dt>
                <dd className="mt-1 text-xs font-medium uppercase tracking-wider text-[#474D57]">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative lg:col-span-5">
          <div
            data-testid="hero-card"
            className="animate-float-up delay-200 relative ml-auto max-w-md rounded-2xl border border-[#0A0B10]/10 bg-white p-6 shadow-[0_25px_80px_-30px_rgba(75,63,158,0.4)]"
          >
            <div className="flex items-center justify-between border-b border-dashed border-[#0A0B10]/10 pb-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#474D57]">
                <Lock className="h-3.5 w-3.5" /> Device control
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{" "}
                Active
              </span>
            </div>
            <div className="mt-5 flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#0A0B10] text-white">
                <Smartphone className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg font-bold tracking-tight">
                  Galaxy A55 5G
                </div>
                <div className="text-sm text-[#474D57]">
                  IMEI ••••• 482 · Customer #4821
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-[#F4F5F7] p-3">
                <div className="font-display text-xl font-extrabold">
                  ₹4,200
                </div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-[#474D57]">
                  EMI / mo
                </div>
              </div>
              <div className="rounded-lg bg-[#F4F5F7] p-3">
                <div className="font-display text-xl font-extrabold">7/12</div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-[#474D57]">
                  Paid
                </div>
              </div>
              <div className="rounded-lg bg-[#4B3F9E] p-3 text-white">
                <div className="font-display text-xl font-extrabold">
                  On-time
                </div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest opacity-80">
                  Status
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button className="flex-1 rounded-md bg-[#0A0B10] py-2.5 text-sm font-semibold text-white">
                Send reminder
              </button>
              <button className="flex-1 rounded-md border border-[#0A0B10]/15 py-2.5 text-sm font-semibold text-[#0A0B10]">
                Lock device
              </button>
            </div>
          </div>
          <div className="absolute -left-6 -top-6 hidden h-24 w-24 rounded-2xl border border-[#FFC107]/40 bg-[#FFC107]/20 lg:block" />
          <div className="absolute -bottom-6 -right-6 hidden h-32 w-32 rounded-full border border-[#1C9AA0]/30 bg-[#1C9AA0]/10 lg:block" />
        </div>
      </div>
    </div>
  </section>
);

const Products = () => (
  <section id="products" data-testid="products-section" className="bg-white py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <SectionLabel num="01">Our Products</SectionLabel>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight text-[#0A0B10] sm:text-5xl">
            One platform.{" "}
            <span className="text-[#1C9AA0]">Three powerful lockers.</span>
          </h2>
        </div>
        <p className="text-base text-[#474D57] lg:col-span-5">
          A complete EMI device-management suite — built for mobile and
          appliance retailers across India to protect financed inventory.
        </p>
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.map((p, idx) => {
          const Icon = p.Icon;
          return (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#0A0B10]/10 bg-white p-7 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: p.accent }}
              />
              <div className="flex items-start justify-between">
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${p.accent}14`, color: p.accent }}
                >
                  <Icon className="h-7 w-7" />
                </div>
                <span className="font-mono text-xs font-semibold uppercase tracking-widest text-[#0A0B10]/40">
                  0{idx + 1} / {p.tag}
                </span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#474D57]">
                {p.desc}
              </p>
              <ul className="mt-6 space-y-2.5 border-t border-dashed border-[#0A0B10]/10 pt-5">
                {p.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-[#0A0B10]"
                  >
                    <Check
                      className="mt-0.5 h-4 w-4 shrink-0"
                      style={{ color: p.accent }}
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`product-cta-${p.id}`}
                className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A0B10] transition group-hover:gap-3"
                style={{ color: p.accent }}
              >
                Request a demo <ArrowRight className="h-4 w-4" />
              </a>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section id="why-us" data-testid="why-us-section" className="relative bg-[#F4F5F7] py-24 lg:py-32">
    <div className="absolute inset-0 grid-lines opacity-50" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-3xl">
        <SectionLabel num="02">Why SafetyPay</SectionLabel>
        <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight text-[#0A0B10] sm:text-5xl">
          A few good reasons retailers <em className="not-italic text-[#4B3F9E]">choose us</em>.
        </h2>
      </div>

      <div className="mt-14 grid border-l border-t border-[#0A0B10]/10 md:grid-cols-3">
        {REASONS.map((r, i) => {
          const Icon = r.Icon;
          return (
            <div
              key={r.title}
              data-testid={`reason-${i}`}
              className="group relative border-b border-r border-[#0A0B10]/10 bg-white p-8 transition hover:bg-[#0A0B10] hover:text-white"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#4B3F9E]/10 text-[#4B3F9E] transition group-hover:bg-white/10 group-hover:text-[#FFC107]">
                  <Icon className="h-6 w-6" />
                </div>
                <span className="font-mono text-xs font-semibold text-[#0A0B10]/40 group-hover:text-white/50">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-8 font-display text-2xl font-bold tracking-tight">
                {r.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#474D57] group-hover:text-white/70">
                {r.desc}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Testimonials = () => {
  const items = [...TESTIMONIALS, ...TESTIMONIALS];
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="overflow-hidden bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel num="03">Client stories</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              What clients say <span className="text-[#1C9AA0]">about us.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-[#474D57] lg:col-span-5 lg:justify-end">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-[#FFC107] text-[#FFC107]"
                />
              ))}
            </div>
            Rated 4.9 / 5 by 500+ retailers
          </div>
        </div>
      </div>

      <div className="relative mt-14">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-gradient-to-l from-white to-transparent" />
        <div className="flex w-max animate-marquee gap-6 px-6 lg:px-10">
          {items.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="flex w-[360px] shrink-0 flex-col justify-between rounded-2xl border border-[#0A0B10]/10 bg-white p-7 sm:w-[420px]"
            >
              <blockquote className="text-base leading-relaxed text-[#0A0B10]">
                <span className="font-display text-3xl leading-none text-[#4B3F9E]">
                  &ldquo;
                </span>
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-dashed border-[#0A0B10]/10 pt-5">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full font-display text-base font-bold text-white"
                  style={{
                    backgroundColor: i % 2 === 0 ? "#4B3F9E" : "#1C9AA0",
                  }}
                >
                  {t.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <div className="font-display text-sm font-bold tracking-tight">
                    {t.name}
                  </div>
                  <div className="text-xs text-[#474D57]">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0B10] py-24 text-white lg:py-32"
    >
      <div className="absolute inset-0 opacity-[0.07]">
        <div className="h-full w-full grid-lines" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#FFC107]">
              <span className="font-mono text-white/40">04</span>
              <span className="h-px w-8 bg-[#FFC107]/40" />
              Contact
            </div>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              Let's secure your <br />
              <span className="text-[#1C9AA0]">retail business.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/70">
              Tell us about your store and how many EMI customers you manage —
              we'll set you up with the right Air Lock product.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/5">
                  <Phone className="h-4 w-4 text-[#FFC107]" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">
                    Phone
                  </div>
                  <div className="mt-1 space-y-0.5 text-base font-medium">
                    <a
                      href="tel:+919266866302"
                      data-testid="contact-phone-1"
                      className="block hover:text-[#FFC107]"
                    >
                      +91 9266866302
                    </a>
                    <a
                      href="tel:+919266866301"
                      data-testid="contact-phone-2"
                      className="block hover:text-[#FFC107]"
                    >
                      +91 9266866301
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/5">
                  <Mail className="h-4 w-4 text-[#FFC107]" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">
                    Email
                  </div>
                  <a
                    href="mailto:support@thesafetypay.com"
                    data-testid="contact-email"
                    className="mt-1 block text-base font-medium hover:text-[#FFC107]"
                  >
                    support@thesafetypay.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/5">
                  <MapPin className="h-4 w-4 text-[#FFC107]" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">
                    Address
                  </div>
                  <a
                    href="https://maps.app.goo.gl/kP4U4pvsHEgDVuuf7"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-address"
                    className="mt-1 block max-w-sm text-base leading-relaxed font-medium text-white/90 hover:text-[#FFC107]"
                  >
                    1st Floor, C-162, Shalimar Garden Extn-2, Sahibabad,
                    Ghaziabad, Uttar Pradesh, 201005
                  </a>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur lg:col-span-7"
          >
            <h3 className="font-display text-2xl font-bold tracking-tight">
              Send us a message
            </h3>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                  Full name *
                </label>
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-2 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-[#FFC107]"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                  Email *
                </label>
                <Input
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-2 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-[#FFC107]"
                  placeholder="you@store.com"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                  Phone
                </label>
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-2 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-[#FFC107]"
                  placeholder="+91 ..."
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                  Subject
                </label>
                <Input
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  className="mt-2 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-[#FFC107]"
                  placeholder="Product enquiry"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                Message *
              </label>
              <Textarea
                rows={5}
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="mt-2 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-[#FFC107]"
                placeholder="Tell us about your store and what you're looking for…"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FFC107] py-6 text-base font-semibold text-[#0A0B10] hover:bg-[#FFD54F] disabled:opacity-60 sm:w-auto sm:px-8"
            >
              {submitting ? "Sending…" : "Send message"}
              <ArrowUpRight className="h-4 w-4" />
            </Button>
            <p className="mt-3 text-xs text-white/50">
              We typically respond within one business day.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative border-t border-white/10 bg-[#0A0B10] text-white"
  >
    <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-5">
        <div className="flex items-center gap-2">
          <div className="flex h-50 50 items-center justify-center rounded-md">
            <img src={LOGO_URL} alt="SafetyPay" className="h-7 w-100" />
          </div>
        </div>
        <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
          EMI locker solutions for Indian retailers. Secure your financed
          Android phones, iPhones and appliances with confidence.
        </p>
      </div>
      <div className="lg:col-span-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
          Products
        </div>
        <ul className="mt-4 space-y-2 text-sm text-white/80">
          {PRODUCTS.map((p) => (
            <li key={p.id}>
              <a href="#products" className="hover:text-[#FFC107]">
                {p.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
          Reach us
        </div>
        <ul className="mt-4 space-y-2 text-sm text-white/80">
          <li>
            <a
              href="mailto:support@thesafetypay.com"
              className="hover:text-[#FFC107]"
            >
              support@thesafetypay.com
            </a>
          </li>
          <li>
            <a href="tel:+919266866302" className="hover:text-[#FFC107]">
              +91 9266866302
            </a>
          </li>
          <li className="text-white/60">
            Shalimar Garden Extn-2, Sahibabad, Ghaziabad, UP — 201005
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-6 text-xs text-white/50 sm:flex-row sm:items-center lg:px-10">
        <div data-testid="footer-copyright">
          © {new Date().getFullYear()} SafetyPay. All rights reserved.
        </div>
        <div
          data-testid="powered-by"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 font-semibold uppercase tracking-[0.2em] text-white/80"
        >
          <Sparkles className="h-3 w-3 text-[#FFC107]" />
          Powered by AIRVOICE TECHNOLOGIES
        </div>
      </div>
    </div>
  </footer>
);

export default function SafetyPayPage() {
  return (
    <div className="font-body text-[#0A0B10]">
      <Header />
      <main>
        <Hero />
        <Products />
        <WhyUs />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
